import fileStackClient from '@app/filestack';
import type { AssetFileDoc, AssetFolderDoc } from '@bb/domain/dist/schemas';
import { Flex, SemanticColor } from '@bb/elements-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteFile,
  getFiles,
  getFolders,
  uploadFiles,
} from 'api/AssetController';
import { message } from 'components';
import { maxBy, orderBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import { isValidUrl } from 'utils';
import {
  ASSETS_PER_TYPE_PAGE_LIMIT,
  DISALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES_UPLOAD,
} from '../constants';
import AddVideoModal from '../Modals/AddVideoModal';
import VideoPlayerModal from '../Modals/VideoPlayerModal';
import AssetExplorerHeader from './AssetExplorerHeader';
import AssetExplorerList from './AssetExplorerList';

type AssetExplorerProps = {
  folderId?: string;
  isAdmin: boolean;
  currAssetsPage: number;
  onPageChange: (page: number) => void;
  onFolderSettingsClick: (folder: AssetFolderDoc) => void;
};

export type AssetExplorerView = 'grid' | 'list';

const AssetExplorer = ({
  folderId,
  isAdmin,
  currAssetsPage,
  onPageChange,
  onFolderSettingsClick,
}: AssetExplorerProps) => {
  const [explorerView, setExplorerView] = useState<AssetExplorerView>('list');
  const history = useHistory();
  const queryClient = useQueryClient();

  const pageSize = useMemo(() => (isAdmin ? 15 : 10), [isAdmin]);
  const [isAddVideoModalVisible, setIsAddVideoModalVisible] = useState(false);
  const [videoPlayData, setVideoPlayData] = useState<AssetFileDoc | null>(null);

  const { data: assets, isFetching: isFetchingAssets } = useQuery({
    queryKey: ['assets', folderId, currAssetsPage, isAdmin],
    queryFn: async () => {
      const params = {
        folderId,
        limit: ASSETS_PER_TYPE_PAGE_LIMIT,
        page: currAssetsPage,
      };

      const queries = [
        getFolders({ ...params, isAdmin: false }),
        getFiles(params),
      ];

      if (isAdmin) {
        queries.push(getFolders({ ...params, isAdmin: true }));
      }

      return Promise.all(queries);
    },
    select: results => {
      const [folders, files, adminFolders] = results;

      // Show folders first sorted, then files sorted
      let resultAssets: (AssetFolderDoc | AssetFileDoc)[] = orderBy(
        folders.assets.concat(adminFolders?.assets || []),
        [asset => asset.name.toLowerCase()],
        ['asc'],
      );
      resultAssets = resultAssets.concat(
        orderBy(files.assets, 'name', 'asc') as AssetFileDoc[],
      );

      // Necessary to make pagination work correctly:
      // use biggest totalAssets count to calculate totalAssets across pages
      const resultTotalAssets =
        maxBy(results, 'totalAssets').totalAssets * results.length;

      return {
        assets: resultAssets,
        totalAssets: resultTotalAssets,
      };
    },
    keepPreviousData: true,
  });

  const { mutate: mutateUploadFiles } = useMutation({
    mutationFn: (result: any) => uploadFiles(result.filesUploaded, folderId),
    onSuccess: (_, result) => {
      message.success(
        `File${result.filesUploaded.length > 1 ? 's' : ''} uploaded successfully`,
      );
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: () => {
      message.error('File upload failed');
    },
  });

  const handleFileUpload = useCallback(() => {
    const opts = {
      maxFiles: MAX_FILES_UPLOAD,
      maxSize: MAX_FILE_SIZE_BYTES,
      onFileSelected: (file: any) => {
        const fileType = file.mimetype.split('/')[1];
        if (DISALLOWED_FILE_TYPES.includes(fileType)) {
          throw new Error(`The file type ${fileType} is not allowed.`);
        }
        if (file.source === 'url' && !isValidUrl(file.originalPath)) {
          throw new Error('Invalid URL format. Please try again.');
        }
      },
      onUploadDone: (result: any) => {
        if (!result.filesUploaded || result.filesUploaded.length === 0) {
          message.error('No files were uploaded.');
          return;
        }
        mutateUploadFiles(result);
      },
    };

    const picker = fileStackClient.picker(opts);
    picker.open();
  }, [mutateUploadFiles]);

  const handleNewAssetClick = useCallback(
    (assetType: 'folder' | 'fileUpload' | 'videoLink') => {
      // Handle new asset creation based on assetType
      switch (assetType) {
        case 'fileUpload':
          handleFileUpload();
          break;
        case 'folder':
          // Work around for AssetFolderDoc.parent being type ObjectId
          onFolderSettingsClick({ parent: folderId as any } as AssetFolderDoc);
          break;
        case 'videoLink':
          setIsAddVideoModalVisible(true);
          break;
        default:
          // this should never be thrown
          throw new Error(`Invalid asset type: ${assetType as never}`);
      }
    },
    [folderId, onFolderSettingsClick, handleFileUpload],
  );

  const handleAssetSelect = useCallback(
    ({ id, url }: { id: string; url?: string }) => {
      if (url) {
        window.open(url, '_blank');
        return;
      }
      onPageChange(1);
      history.push(`/assets/?folderId=${id}`);
    }, // excluding history because it's stable
    [onPageChange],
  );

  const handleListAssetSelect = useCallback(
    (asset: AssetFolderDoc | AssetFileDoc, filetype: string) => {
      if (filetype === 'application/x-shockwave-flash') {
        setVideoPlayData(asset as AssetFileDoc);
      } else {
        handleAssetSelect({ id: asset.id, url: (asset as AssetFileDoc).url });
      }
    },
    [handleAssetSelect],
  );

  const handleActionClick = useCallback(
    (asset: AssetFolderDoc | AssetFileDoc) => {
      const filetype = (asset as AssetFileDoc).filetype || 'folder';
      if (filetype === 'folder') {
        onFolderSettingsClick(asset as AssetFolderDoc);
        return;
      }

      Swal.fire({
        title: 'Delete File',
        text: 'Are you sure you want to permanently delete this file?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: SemanticColor.success,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
      }).then(async result => {
        if (result.value) {
          await deleteFile(asset.id);
          queryClient.invalidateQueries({ queryKey: ['assets'] });
        }
      });
    }, // excluding queryClient because it's stable
    [onFolderSettingsClick],
  );

  return (
    <>
      <Flex vertical gap={5}>
        <AssetExplorerHeader
          isAdmin={isAdmin}
          explorerView={explorerView}
          onViewChange={setExplorerView}
          onNewAssetClick={handleNewAssetClick}
          onSearchSelect={handleAssetSelect}
        />
        <AssetExplorerList
          actionsEnabled={isAdmin}
          explorerView={explorerView}
          assets={assets?.assets || []}
          currPage={currAssetsPage}
          loading={isFetchingAssets}
          onPageChange={onPageChange}
          onAssetClick={handleListAssetSelect}
          onActionClick={handleActionClick}
          pageSize={pageSize}
          totalAssets={assets?.totalAssets || 0}
        />
      </Flex>
      <AddVideoModal
        parent={folderId}
        visible={isAddVideoModalVisible}
        onClose={() => setIsAddVideoModalVisible(false)}
      />
      <VideoPlayerModal
        visible={!!videoPlayData?.url}
        file={videoPlayData}
        isAdmin={isAdmin}
        onClose={() => setVideoPlayData(null)}
      />
    </>
  );
};

export default AssetExplorer;
